import liff from "@line/liff";
import { UserEventApi } from "@utils/api/analytics/user-event.api";
import type {
	UserEventDTO,
	UserLoginPageDTO,
} from "@utils/api/analytics/user-event.dto";

type LogEventInput = Omit<UserEventDTO, "ts">;
type LogPageInput = Partial<Omit<UserLoginPageDTO, "ts">>;

const DATA_TOKEN_KEY = "DATA_TOKEN";

export function useUserEvent() {
	/**
	 * 取得 analytics 用的 DATA_TOKEN：
	 * - 若 localStorage 已有則直接 return；
	 * - 否則用目前 LIFF profile + accessToken 換一張新 JWT 寫入 localStorage。
	 *
	 * 適合給 LoginGuard 之外的頁面（例如 campaign pages）在 liff.init 完成後呼叫一次。
	 */
	const ensureToken = async () => {
		if (localStorage.getItem(DATA_TOKEN_KEY)) return;
		const accessToken = liff.getAccessToken() ?? "";
		const profile = await liff.getProfile();
		const res = await UserEventApi.loginEvent({
			line_uid: profile.userId,
			line_name: profile.displayName,
			liff_id: process.env.REACT_APP_LIFF_ID ?? "",
			access_token: accessToken,
		});
		if (res?.jwt) {
			localStorage.setItem(DATA_TOKEN_KEY, res.jwt);
		}
	};

	const logEvent = async (data: LogEventInput) => {
		const payload: UserEventDTO = {
			...data,
			ts: Math.floor(Date.now() / 1000),
		};

		return UserEventApi.logEvent(payload);
	};

	const logPage = async (data?: LogPageInput) => {
		const payload: UserLoginPageDTO = {
			path: data?.path ?? window.location.pathname,
			ts: Math.floor(Date.now() / 1000),
		};

		return UserEventApi.logPage(payload);
	};

	// EC 專用的 path 正規化（其他頁面用不到就不必呼叫）
	const normalizeLogPath = (): string => {
		let pathname = window.location.pathname;
		if (pathname.includes("/series/")) {
			let id = pathname.split("/series/")[1];
			return `/productlist/category_id/${id}`;
		}

		if (pathname.includes("/product/")) {
			let id = pathname.split("/product/")[1];
			return `/product/item_id/${id}`;
		}

		if (pathname.includes("collectList")) {
			return "/wishlist";
		}

		if (pathname == "/ec") {
			return "/homepage";
		}

		return pathname;
	};

	return {
		ensureToken,
		logEvent,
		logPage,
		normalizeLogPath,
	};
}
